import { get, merge, has } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Line',
  displayName: 'Discrete',
  order: 0,
  flavour: 'discrete',
  category: CHART_CATEGORIES.LINE,
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  iconValue: 'line-discrete',
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const logScaleOnValueAxis = get(
      customisations.axes,
      'y.logScale.enabled',
      false
    );
    // do not pass logScale because we always want Vega to
    // decide the min value even if log scale has been enabled
    const valueAxisProps = getScaleMinMaxProps(customisations, 'y');
    const showDataMarkers = get(customisations.options, 'dataMarkers.enabled');
    return {
      autosize: { type: 'fit', contains: 'padding' },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('y'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
          ],
        },
      ],
      marks: [
        {
          name: 'pathgroup',
          type: 'group',
          from: {
            facet: {
              name: 'faceted_path_main',
              data: 'transformedData',
              groupby: ['color'],
            },
          },
          encode: {
            update: {
              width: { field: { group: 'width' } },
              height: { field: { group: 'height' } },
            },
          },
          marks: [
            {
              name: 'marks',
              type: 'line',
              style: ['line'],
              sort: sortParams.marks,
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  x: { scale: 'x', field: 'x' },
                  y: { scale: 'y', field: 'y' },
                  stroke: { scale: 'color', field: 'color' },
                },
              },
            },
            {
              type: 'symbol',
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  x: { scale: 'x', field: 'x' },
                  y: { scale: 'y', field: 'y' },
                  fill: showDataMarkers
                    ? { scale: 'color', field: 'color' }
                    : { value: 'transparent' },
                  stroke: {
                    scale: 'color',
                    field: 'color',
                  },
                  strokeWidth: { value: showDataMarkers ? 1.5 : 0 },
                  size: { value: 30 },
                  tooltip: {
                    signal: generateVegaTooltip(encodings, customisations),
                  },
                },
              },
            },
            ...(!get(customisations.options, 'dataValueLabels.enabled')
              ? []
              : [
                  {
                    type: 'text',
                    name: 'valueLabels',
                    from: { data: 'faceted_path_main' },
                    encode: {
                      enter: {
                        text: {
                          signal: generateVegaLabelTextExpression(
                            encodings.y,
                            customisations.channels.y,
                            { field: 'y' }
                          ),
                        },
                        dy: { value: showDataMarkers ? -6 : -3 },
                        x: { scale: 'x', field: 'x' },
                        y: { scale: 'y', field: 'y' },
                        align: { value: 'center' },
                      },
                    },
                  },
                ]),
          ],
        },
      ],
      scales: [
        {
          name: 'x',
          type: 'point',
          domain: { data: 'transformedData', field: 'x', sort: sortParams.x },
          range: [0, { signal: 'width' }],
          padding: 0.5,
        },
        {
          name: 'y',
          type: logScaleOnValueAxis ? 'log' : 'linear',
          domain: { data: 'transformedData', field: 'y', sort: false },
          range: [{ signal: 'height' }, 0],
          zero: !logScaleOnValueAxis,
          ...valueAxisProps,
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                angle: { value: 270 },
                align: { value: 'right' },
                baseline: { value: 'middle' },
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          title: get(encodings, 'y.axis.title', ''),
          tickCount: { signal: 'ceil(height/40)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              fill: 'color',
              title: get(encodings, 'color.legend.title'),
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
    };
  },
};
